import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { EmojiPicker } from '../components/EmojiPicker';
import { MessageSquare, Pin, SmilePlus, Trash2, Check, CheckCheck, Download, Forward, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ForwardDialog from './ForwardDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessageList = ({ messages, onThreadClick, channelId, onUserClick }) => {
  const { user } = useAuth();
  const { sendReadReceipt } = useChat();
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardAtt, setForwardAtt] = useState(null);
  const [forwardSender, setForwardSender] = useState('');

  const handleForward = (att, senderName) => {
    setForwardAtt(att);
    setForwardSender(senderName);
    setForwardOpen(true);
  };

  const handleDownload = async (att) => {
    const url = att.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${att.url}` : att.url;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = att.original_name || att.name || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch {
      // Fallback: open in new tab
      window.open(url, '_blank');
    }
  };

  const handleShare = async (att) => {
    const url = att.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${att.url}` : att.url;
    const fileName = att.original_name || att.name || 'file';

    // Try Web Share API with file (best experience)
    if (navigator.share) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: blob.type });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: fileName });
          return;
        }
        // Fallback: share URL only
        await navigator.share({ title: fileName, url: url });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return; // User cancelled
      }
    }
    // Final fallback: copy link
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not share file');
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Read receipt on new messages
  useEffect(() => {
    if (!messages.length || !channelId) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg && lastMsg.sender_id !== user?.user_id && !lastMsg.read_by?.includes(user?.user_id)) {
      sendReadReceipt(channelId, lastMsg.message_id);
    }
  }, [messages, channelId, user, sendReadReceipt]);

  const handlePin = useCallback(async (messageId) => {
    try {
      const res = await axios.post(`${API}/messages/${messageId}/pin`);
      toast.success(res.data.pinned ? 'Message pinned' : 'Message unpinned');
    } catch (e) {
      toast.error('Failed to pin message');
    }
  }, []);

  const handleReaction = useCallback(async (messageId, emoji) => {
    try {
      await axios.post(`${API}/messages/${messageId}/react`, { emoji });
    } catch (e) {
      toast.error('Failed to add reaction');
    }
  }, []);

  const handleDelete = useCallback(async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      toast.success('Message deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete message');
    }
  }, []);

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (ts) => {
    const d = new Date(ts);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  };

  // Group messages by date
  let lastDate = null;

  return (
    <ScrollArea className="h-full" ref={scrollRef}>
      <div className="px-3 sm:px-5 py-2" role="list" data-testid="message-list">
        {messages.map((msg, idx) => {
          const msgDate = new Date(msg.timestamp).toDateString();
          const showDate = msgDate !== lastDate;
          lastDate = msgDate;

          // Check if mention for current user
          const isMentioned = msg.mentions?.some(m => m.user_id === user?.user_id);

          return (
            <React.Fragment key={msg.message_id}>
              {showDate && (
                <div className="flex items-center gap-3 my-3" data-testid="date-separator">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs font-medium text-muted-foreground">{formatDate(msg.timestamp)}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
              )}
              <div
                className={`group relative flex gap-3 px-2 py-1.5 -mx-2 rounded-lg transition-colors duration-150 hover:bg-[hsl(var(--message-hover))] ${
                  msg.deleted ? 'opacity-60' : ''
                } ${isMentioned && !msg.deleted ? 'bg-[hsl(var(--mention)/0.3)] border-l-2 border-[hsl(var(--accent-foreground)/0.3)]' : ''
                } ${msg.pinned && !msg.deleted ? 'bg-[hsl(var(--primary)/0.04)]' : ''}`}
                role="listitem"
                data-testid={`message-row-${msg.message_id}`}
              >
                {/* Avatar */}
                <button
                  className="w-8 h-8 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5 hover:ring-2 hover:ring-[hsl(var(--primary)/0.3)] transition-shadow cursor-pointer"
                  onClick={() => onUserClick?.(msg.sender_id)}
                  data-testid={`message-avatar-${msg.message_id}`}
                >
                  {msg.sender_name?.[0] || '?'}
                </button>

                {/* Message body */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <button
                      className="text-sm font-semibold hover:underline cursor-pointer"
                      onClick={() => onUserClick?.(msg.sender_id)}
                      data-testid={`message-sender-${msg.message_id}`}
                    >
                      {msg.sender_name || 'Unknown'}
                    </button>
                    <span className="text-xs text-muted-foreground">{formatTime(msg.timestamp)}</span>
                    {msg.pinned && !msg.deleted && (
                      <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">
                        <Pin className="w-2.5 h-2.5 mr-0.5" /> Pinned
                      </Badge>
                    )}
                    {msg.edited && !msg.deleted && (
                      <span className="text-xs text-muted-foreground">(edited)</span>
                    )}
                  </div>

                  {/* Deleted message display */}
                  {msg.deleted ? (
                    <div className="text-sm italic text-muted-foreground flex items-center gap-1.5" data-testid={`message-deleted-${msg.message_id}`}>
                      <Trash2 className="w-3 h-3" />
                      This message was deleted
                    </div>
                  ) : (
                    <>
                      {/* Message content */}
                      {msg.message_type === 'note' && msg.note ? (
                        <div className="bg-[hsl(var(--accent)/0.5)] rounded-lg p-3 mt-1 border border-[hsl(var(--accent))]" data-testid={`note-card-${msg.message_id}`}>
                          <p className="text-sm font-semibold mb-1" style={{fontFamily: 'Space Grotesk'}}>{msg.note.title}</p>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{msg.note.body}</p>
                        </div>
                      ) : msg.message_type === 'code' && msg.code_snippet ? (
                        <div className="mt-1" data-testid={`code-block-${msg.message_id}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="text-[10px] h-4">{msg.code_snippet.language || 'code'}</Badge>
                          </div>
                          <pre className="bg-[hsl(var(--surface-2))] rounded-lg p-3 text-sm overflow-x-auto border border-border">
                            <code style={{fontFamily: 'IBM Plex Mono, monospace'}}>{msg.code_snippet.code}</code>
                          </pre>
                        </div>
                      ) : (
                        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words" data-testid={`message-content-${msg.message_id}`}>
                          {renderMessageContent(msg.content, msg.mentions, user)}
                        </div>
                      )}

                      {/* Attachments with actions */}
                      {msg.attachments?.length > 0 && (
                        <div className="space-y-1.5 mt-2">
                          {msg.attachments.map((att, i) => {
                            const fileUrl = att.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${att.url}` : att.url;
                            const isImage = att.file_type?.startsWith('image/');
                            const isVideo = att.file_type?.startsWith('video/');
                            return (
                              <div key={i} className="rounded-lg border border-border overflow-hidden bg-[hsl(var(--surface-2))]" data-testid={`attachment-${msg.message_id}-${i}`}>
                                {isImage && (
                                  <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                    <img src={fileUrl} alt={att.original_name || att.name} className="max-w-[300px] max-h-[200px] object-cover" />
                                  </a>
                                )}
                                {isVideo && (
                                  <video src={fileUrl} controls className="max-w-[300px] max-h-[200px]" />
                                )}
                                <div className="flex items-center gap-1.5 px-2 py-1.5">
                                  <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[hsl(var(--primary))] hover:underline truncate flex-1">
                                    {att.original_name || att.name || 'File'}
                                  </a>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDownload(att)} data-testid={`download-att-${msg.message_id}-${i}`}>
                                        <Download className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Download</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleForward(att, msg.sender_name)} data-testid={`forward-att-${msg.message_id}-${i}`}>
                                        <Forward className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Forward</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleShare(att)} data-testid={`share-att-${msg.message_id}-${i}`}>
                                        <Share2 className="w-3 h-3" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Share</TooltipContent>
                                  </Tooltip>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Reactions */}
                      {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`reactions-${msg.message_id}`}>
                          {Object.entries(msg.reactions).map(([emoji, userIds]) => {
                            const hasReacted = userIds?.includes(user?.user_id);
                            return (
                              <button
                                key={emoji}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors duration-150 ${
                                  hasReacted
                                    ? 'bg-[hsl(var(--primary)/0.10)] border-[hsl(var(--primary)/0.3)] text-[hsl(var(--primary))]'
                                    : 'bg-[hsl(var(--surface-2))] border-border hover:bg-[hsl(var(--surface-3))]'
                                }`}
                                onClick={() => handleReaction(msg.message_id, emoji)}
                                data-testid={`reaction-${msg.message_id}-${emoji}`}
                              >
                                <span>{emoji}</span>
                                <span className="font-medium">{userIds?.length || 0}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Thread info */}
                      {msg.reply_count > 0 && (
                        <button
                          className="flex items-center gap-1.5 mt-1.5 text-xs text-[hsl(var(--primary))] hover:underline"
                          onClick={() => onThreadClick?.(msg)}
                          data-testid={`thread-count-${msg.message_id}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                    </>
                  )}

                  {/* Read receipt */}
                  {msg.sender_id === user?.user_id && !msg.deleted && (
                    <div className="flex items-center mt-1">
                      {msg.read_by?.length > 1 ? (
                        <CheckCheck className="w-3 h-3 text-[hsl(var(--primary))]" />
                      ) : (
                        <Check className="w-3 h-3 text-muted-foreground" />
                      )}
                    </div>
                  )}
                </div>

                {/* Hover actions - hide for deleted messages */}
                {!msg.deleted && (
                  <div className="absolute top-0 right-1 hidden group-hover:flex items-center gap-0.5 bg-card border border-border rounded-lg shadow-sm px-1 py-0.5 -translate-y-1/2" data-testid={`message-actions-${msg.message_id}`}>
                    <EmojiPicker
                      onSelect={(emoji) => handleReaction(msg.message_id, emoji)}
                      trigger={
                        <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`add-reaction-${msg.message_id}`}>
                          <SmilePlus className="w-3.5 h-3.5" />
                        </Button>
                      }
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onThreadClick?.(msg)} data-testid={`reply-thread-${msg.message_id}`}>
                          <MessageSquare className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Reply in Thread</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handlePin(msg.message_id)} data-testid={`pin-message-${msg.message_id}`}>
                          <Pin className={`w-3.5 h-3.5 ${msg.pinned ? 'text-[hsl(var(--primary))]' : ''}`} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{msg.pinned ? 'Unpin' : 'Pin'}</TooltipContent>
                    </Tooltip>
                    {msg.sender_id === user?.user_id && (
                      <AlertDialog>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" data-testid={`delete-message-${msg.message_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                          </TooltipTrigger>
                          <TooltipContent>Delete Message</TooltipContent>
                        </Tooltip>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete message?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This message will be permanently deleted for everyone. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-delete-msg">Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(msg.message_id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              data-testid="confirm-delete-msg"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>
      
      {/* Forward Dialog */}
      <ForwardDialog
        open={forwardOpen}
        onOpenChange={setForwardOpen}
        attachment={forwardAtt}
        senderName={forwardSender}
      />
    </ScrollArea>
  );
};

// Render message content with @mention highlighting
const renderMessageContent = (content, mentions, currentUser) => {
  if (!content) return null;
  if (!mentions || mentions.length === 0) return content;

  const parts = [];
  let lastIndex = 0;

  // Sort mentions by position
  const sortedMentions = [...mentions].sort((a, b) => (a.index || 0) - (b.index || 0));

  for (const mention of sortedMentions) {
    const mentionText = `@${mention.display_name || mention.username}`;
    const idx = content.indexOf(mentionText, lastIndex);
    if (idx === -1) continue;

    if (idx > lastIndex) {
      parts.push(content.slice(lastIndex, idx));
    }

    const isSelf = mention.user_id === currentUser?.user_id;
    parts.push(
      <span
        key={`mention-${idx}`}
        className={`inline-block px-1 rounded text-sm font-medium ${
          isSelf
            ? 'bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))]'
            : 'bg-[hsl(var(--accent)/0.5)] text-[hsl(var(--accent-foreground))]'
        }`}
      >
        {mentionText}
      </span>
    );
    lastIndex = idx + mentionText.length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : content;
};

export default MessageList;
